import svgUrl from './assets/Pine_Island_case_study.svg?url';

const VIEW_W = 370;
const VIEW_H = 337.1;

const STYLES = `
  :host {
    display: block;
    width: 100%;
    position: relative;
    user-select: none;
  }

  .infographic {
    position: relative;
  }

  .infographic-svg {
    display: block;
    padding-bottom: 4px;
    cursor: default;
  }

  /* Positioning context that exactly matches the SVG viewBox aspect ratio,
     so percentage-based button positions align 1:1 with SVG coordinates. */
  .infographic-map {
    position: relative;
    width: 100%;
    aspect-ratio: 370 / 337.1;
  }

  .infographic-svg svg {
    display: block;
    width: 100%;
    height: 100%;
  }

  .tooltip {
    position: absolute;
    z-index: 10;
    transform: translate(-50%, calc(-100% - 14px));
    max-width: 240px;
    min-width: 180px;
    background: var(--xe-color-surface-base);
    border: 1px solid var(--xe-color-border-neutral);
    border-radius: var(--xe-border-radius-lg, 12px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    padding: 12px 14px;
    pointer-events: auto;
    color: var(--xe-color-content-on-surface-on-surface);
  }

  .tooltip--below {
    transform: translate(-50%, 14px);
  }

  /* HTML buttons positioned over SVG nodes */
  .node-btn {
    position: absolute;
    width: calc(72 / 370 * 100%);
    aspect-ratio: 1;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    outline: none;
    transition: background 0.15s ease;
  }

  .node-btn:hover {
    background: color-mix(in srgb, var(--xe-color-content-on-neutral-container) 15%, transparent);
  }

  .node-btn:focus-visible {
    background: color-mix(in srgb, var(--xe-color-content-on-neutral-container) 15%, transparent);
    outline: 2px solid var(--xe-color-border-focus, currentColor);
    outline-offset: 3px;
  }

  /* Dark mode: brand.primary elements (icons, numbers, center circle) become white */
  @media (prefers-color-scheme: dark) {
    .infographic-svg .st12,
    .infographic-svg .st9 { fill: var(--xe-color-surface-fixed-light) !important; }
    .infographic-svg .st1,
    .infographic-svg .st6,
    .infographic-svg .st7 { stroke: var(--xe-color-surface-fixed-light) !important; }
  }

  :host-context([data-theme="dark"]) .infographic-svg .st12,
  :host-context([data-theme="dark"]) .infographic-svg .st9 {
    fill: var(--xe-color-surface-fixed-light) !important;
  }

  :host-context([data-theme="dark"]) .infographic-svg .st1,
  :host-context([data-theme="dark"]) .infographic-svg .st6,
  :host-context([data-theme="dark"]) .infographic-svg .st7 {
    stroke: var(--xe-color-surface-fixed-light) !important;
  }

  .tooltip-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
  }

  .tooltip-title {
    font-family: var(--xe-typography-label-md-family, sans-serif);
    font-size: var(--xe-typography-label-md-size, 14px);
    font-weight: var(--xe-typography-label-md-weight, 600);
    line-height: var(--xe-typography-label-md-line-height, 1.4);
    color: var(--xe-color-content-on-surface-on-surface);
    margin: 0;
  }

  .tooltip-close {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--xe-color-content-on-surface-on-surface-subtle);
    font-size: 16px;
    line-height: 1;
    border-radius: 4px;
  }

  .tooltip-close:hover {
    background: var(--xe-color-surface-container-base);
  }

  .tooltip-body {
    margin: 0;
    font-family: var(--xe-typography-body-sm-family, sans-serif);
    font-size: var(--xe-typography-body-sm-size, 13px);
    line-height: var(--xe-typography-body-sm-line-height, 1.5);
    color: inherit;
  }
`;

class PineIslandInfographic extends HTMLElement {
  constructor() {
    super();
    this._nodes = [];
    this._svgContent = null;
    this._activeId = null;
    this._tooltipEl = null;
    this._triggerEl = null;
    this._outsideListener = null;
    this._keyListener = null;

    const shadow = this.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = STYLES;
    shadow.appendChild(style);

    this._root = document.createElement('div');
    this._root.className = 'infographic';
    shadow.appendChild(this._root);

    this._svgWrap = document.createElement('div');
    this._svgWrap.className = 'infographic-svg';
    this._root.appendChild(this._svgWrap);

    this._map = null;

    this._root.addEventListener('click', (e) => this._handleClick(e));
  }

  connectedCallback() {
    fetch(svgUrl)
      .then(r => r.text())
      .then(svg => {
        this._svgContent = svg;
        if (this._nodes.length) this._initSvg();
      });
  }

  set nodes(val) {
    this._nodes = val;
    this._initSvg();
  }

  get nodes() {
    return this._nodes;
  }

  _initSvg() {
    if (!this._svgContent) return;
    const svgContent = this._svgContent.replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '');
    this._svgWrap.innerHTML = `<div class="infographic-map">${svgContent}</div>`;
    this._map = this._svgWrap.querySelector('.infographic-map');
    const svg = this._map.querySelector('svg');
    if (!svg) return;
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');

    this._nodeEls = new Map();

    for (const node of this._nodes) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'node-btn';
      btn.setAttribute('aria-label', node.title);
      btn.setAttribute('aria-expanded', 'false');
      btn.dataset.nodeId = node.id;
      btn.style.left = `${(node.cx / VIEW_W) * 100}%`;
      btn.style.top  = `${(node.cy / VIEW_H) * 100}%`;

      this._map.appendChild(btn);
      this._nodeEls.set(node.id, btn);

      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._toggle(node, btn);
      });
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._toggle(node, btn);
        }
      });
    }
  }

  _toggle(node, btn) {
    if (this._activeId === node.id) {
      this._closeTooltip();
    } else {
      this._openTooltip(node, btn);
    }
  }

  _handleClick(e) {
    if (!this._tooltipEl) return;
    if (!e.composedPath().includes(this._tooltipEl)) {
      this._closeTooltip();
    }
  }

  _openTooltip(node, triggerEl) {
    this._closeTooltip();
    this._activeId = node.id;
    this._triggerEl = triggerEl;
    triggerEl?.setAttribute('aria-expanded', 'true');

    const left = (node.cx / VIEW_W) * 100;
    const top = (node.cy / VIEW_H) * 100;
    const below = node.cy / VIEW_H < 0.45;

    const titleId = `tip-title-${node.id}`;

    const tooltip = document.createElement('div');
    tooltip.className = `tooltip${below ? ' tooltip--below' : ''}`;
    tooltip.setAttribute('role', 'dialog');
    tooltip.setAttribute('aria-modal', 'true');
    tooltip.setAttribute('aria-labelledby', titleId);
    tooltip.style.left = `${left}%`;
    tooltip.style.top = `${top}%`;

    const header = document.createElement('div');
    header.className = 'tooltip-header';

    const title = document.createElement('strong');
    title.className = 'tooltip-title';
    title.id = titleId;
    title.textContent = node.title;

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'tooltip-close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this._closeTooltip();
    });

    header.appendChild(title);
    header.appendChild(closeBtn);

    const body = document.createElement('p');
    body.className = 'tooltip-body';
    body.textContent = node.body;

    tooltip.appendChild(header);
    tooltip.appendChild(body);
    this._map.appendChild(tooltip);
    this._tooltipEl = tooltip;

    requestAnimationFrame(() => {
      const containerRect = this._map.getBoundingClientRect();
      const tipRect = tooltip.getBoundingClientRect();
      const pad = 8;
      if (tipRect.left < containerRect.left + pad) {
        tooltip.style.left = `${((tipRect.width / 2) + pad) / containerRect.width * 100}%`;
      } else if (tipRect.right > containerRect.right - pad) {
        tooltip.style.left = `${((containerRect.width - tipRect.width / 2) - pad) / containerRect.width * 100}%`;
      }
      closeBtn.focus();
    });

    this._outsideListener = (e) => {
      if (!e.composedPath().includes(this)) this._closeTooltip();
    };
    this._keyListener = (e) => {
      if (e.key === 'Escape') this._closeTooltip();
    };

    setTimeout(() => {
      document.addEventListener('click', this._outsideListener);
      document.addEventListener('keydown', this._keyListener);
    }, 0);
  }

  _closeTooltip() {
    if (this._tooltipEl) {
      this._tooltipEl.remove();
      this._tooltipEl = null;
    }
    this._triggerEl?.setAttribute('aria-expanded', 'false');
    this._triggerEl?.focus();
    this._triggerEl = null;
    this._activeId = null;
    if (this._outsideListener) {
      document.removeEventListener('click', this._outsideListener);
      document.removeEventListener('keydown', this._keyListener);
      this._outsideListener = null;
      this._keyListener = null;
    }
  }

  disconnectedCallback() {
    this._closeTooltip();
  }
}

customElements.define('pine-island-infographic', PineIslandInfographic);
